/** SignIn — web mirror of the Swift `SignInView`.
 *
 * The screen a user types their OWN Brain credentials into. It drives
 * `AppEnvironment.signInWithCredentials` → `BrainAuth.login` (POST /login →
 * `brain_` token in localStorage → /supabase-token → Supabase JWT). NO
 * credential is baked in; this only collects and forwards what the user types.
 * Shown only on the Supabase backend when signed out — Mock launches straight
 * into the app. */

import { useState, type FormEvent } from 'react';
import { useEnv } from '../app/context';

export function SignIn() {
  const env = useEnv();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canSubmit = email.trim().length > 0 && password.length > 0 && !submitting;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      await env.signInWithCredentials(email.trim(), password);
      // Success transitions the whole app to RootView via authPhase.
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="signin">
      <form className="signin__card" onSubmit={onSubmit}>
        <div className="signin__brand">
          <div className="signin__logo" aria-hidden>
            ⚙︎
          </div>
          <h1 className="signin__title">Factory</h1>
          <p className="signin__subtitle">Sign in with your Brain account</p>
        </div>

        <div className="signin__fields">
          <input
            className="signin__input"
            type="email"
            name="email"
            autoComplete="username"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={submitting}
            autoFocus
          />
          <input
            className="signin__input"
            type="password"
            name="password"
            autoComplete="current-password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={submitting}
          />
        </div>

        {error ? (
          <div className="signin__error" role="alert">
            ⚠ {error}
          </div>
        ) : null}

        <button className="signin__button" type="submit" disabled={!canSubmit}>
          {submitting ? 'Signing in…' : 'Sign In'}
        </button>

        <p className="signin__note">
          Your password is sent only to the Brain to sign in and is never stored in this browser —
          only the resulting token is kept in local storage.
        </p>
      </form>
    </div>
  );
}
